import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useSWRImmutable from 'swr/immutable';

import { Slide } from '@/api/cms';
import { correctLang } from '@/api/cms/base';
import { filterByGroups } from '@/helpers/cms';
import { fetcherCMS } from '@/modules/api';
import {
  CMSSortTypeEnum,
  createCMSQuery,
  getCMSDurationQuery,
  getCMSImageFieldsUrl,
  getCMSSchedulerQuery,
  getCMSShowGuestQuery,
} from '@/modules/cms';
import { useCommonStore } from '@/store';

export const useCMSSlider = () => {
  const {
    i18n: { language },
  } = useTranslation();

  const user = useCommonStore.use.user();
  const isAuthenticated = useCommonStore.use.isAuthenticated();
  const pendingUserPure = useCommonStore.use.pendingUserPure();

  const query = useMemo(() => {
    if (pendingUserPure) return null;
    return createCMSQuery({
      sort: [`order:${CMSSortTypeEnum.ASC}`],
      filters: {
        $and: [
          getCMSSchedulerQuery(),
          getCMSDurationQuery(),
          ...getCMSShowGuestQuery(isAuthenticated),
        ],
      },
      populate: {
        ...getCMSImageFieldsUrl('background_desktop'),
        ...getCMSImageFieldsUrl('background_mobile'),
        ...getCMSImageFieldsUrl('characters_desktop'),
        ...getCMSImageFieldsUrl('characters_mobile'),
      },
      locale: [correctLang(language)],
    });
  }, [language, isAuthenticated, pendingUserPure]);

  const {
    data = [],
    error,
    isLoading,
  } = useSWRImmutable<Slide[]>(
    query ? `/slides-v-3-s?${query}` : null,
    fetcherCMS
  );

  const slides = useMemo(() => {
    if (!data.length) return [];
    // TODO: optimize logic to avoid client-side filterByGroups
    return filterByGroups({ user })(data) as Slide[];
  }, [data, user]);

  return {
    data,
    isLoading,
    isError: error,
    slides,
  };
};
